import React from 'react';
import { Redirect, Route, Switch, withRouter } from 'react-router';
import { RouteComponentProps } from 'react-router-dom';

import '../scss/index.scss';
import '../scss/mediaplayer.scss';
import FrameMain from 'components/frameMain/FrameMain';
import i18n from 'components/i18n';
import LoadingContent from 'lqa-ui/lib/Components/LoadingContent';
import Error404 from 'screens/errors/404/Error404';
import { ControlHub } from '../modules/ControlHub';
import { MicroApp } from '../modules/MicroApp';

import AdminRouter from './AdminRouter';
import PublicRouter from './PublicRouter';
import { FatalError } from 'screens/errors/FatalError';

const Feedback = React.lazy(
  () => import('screens/questionnaire/public/Feedback')
);

export const RouteNotFound = () => (
  <Redirect to={{ state: { notFoundError: true } }} />
);

export const Main = (props) =>
  props.location &&
  props.location.state &&
  props.location.state.notFoundError ? (
    <FrameMain {...props}>
      <Error404 />
    </FrameMain>
  ) : (
    <FrameMain {...props} />
  );

export const CaptureRouteNotFound = withRouter(Main);

export const LocaleContext = React.createContext('en');

export interface IAppProps extends RouteComponentProps<{ locale: string }> {}

export interface IAppState {
  locale: string;
  hasError: boolean;
}

export default class MainRouter extends React.Component<IAppProps, IAppState> {
  state: Readonly<IAppState> = {
    locale: 'en',
    hasError: false,
  };
  private privateSetLocale = this.setLocale.bind(this);
  private knownLocale: string = '';

  async componentDidMount() {
    this.knownLocale = this.props.match.params.locale;
    await i18n.changeLanguage(this.knownLocale);
    this.setState({
      ...this.state,
      locale: this.knownLocale,
    });

    i18n.on('languageChanged', this.privateSetLocale);
  }

  componentWillUnmount() {
    i18n.off('languageChanged', this.privateSetLocale);
  }

  async componentDidUpdate(prevProps) {
    if (this.knownLocale !== this.props.match.params.locale) {
      await i18n.changeLanguage(this.props.match.params.locale);
      this.setLocale();
    }

    if (prevProps.location !== this.props.location) {
      if (this.state.hasError) {
        this.setState({ ...this.state, hasError: false });
      }
    }
  }

  componentDidCatch(error, info) {
    this.setState({ ...this.state, hasError: true });
  }

  setLocale() {
    const lastLocale = this.knownLocale;
    this.knownLocale = i18n.language;
    this.setState({
      ...this.state,
      locale: this.knownLocale,
    });
    if (lastLocale !== this.knownLocale) {
      // TODO: locale setup step 1:
      // this.props.history.push(this.props.location.pathname.replace(`/${lastLocale}/`, `/${this.knownLocale}/`));
    }
  }

  render() {
    const locale = this.state.locale;

    return this.state.hasError ? (
      <FrameMain>
        <FatalError />
      </FrameMain>
    ) : (
      <LocaleContext.Provider value={locale}>
        <React.Suspense fallback={<LoadingContent layout="absolute" />}>
          <Switch>
            <Route path="/feedback" component={Feedback} />
            <Route path="/training-feedback" component={Feedback} />
            <CaptureRouteNotFound>
              <Switch>
                <Route path="/admin/control-hub" component={ControlHub} />
                <Route
                  path="/my-lqa/billing"
                  render={() => (
                    <MicroApp url={Env.MICRO_APP_MY_LQA_BILLING_URL} />
                  )}
                />
                <Route
                  path="/myLQA/my-audit-trends"
                  render={() => (
                    <MicroApp url={Env.MICRO_APP_MY_AUDIT_TRENDS_URL} />
                  )}
                />
                {/* TODO: locale setup step 2: add /:locale before the paths */}
                <Route path="/admin/" component={AdminRouter} />,
                <Route path="/" component={PublicRouter} />
                <RouteNotFound />
              </Switch>
            </CaptureRouteNotFound>
          </Switch>
        </React.Suspense>
      </LocaleContext.Provider>
    );
  }
}
